import React from 'react';
import LegalDocumentLayout, {
  LegalSection,
  LegalSubheading,
  LegalList,
  LegalListItem
} from '../components/legal/LegalDocumentLayout';

const PrivacyPolicyPage = () => (
  <LegalDocumentLayout
    title="Privacy Policy"
    subtitle="How Niko Vibe collects, uses, and protects your personal information when you use our platform."
  >
    <p className="text-gray-700 text-base md:text-[1.05rem] leading-relaxed">
      Niko Vibe (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) respects your privacy. This Privacy Policy explains how we
      collect, use, disclose, and protect personal information when you use our website, purchase tickets,
      or interact with our services (the &quot;Platform&quot;). By using the Platform, you consent to the practices
      described here, subject to applicable law including the Kenya Data Protection Act, 2019.
    </p>

    <LegalSection title="1. Information we collect">
      <LegalSubheading>Information you provide</LegalSubheading>
      <LegalList>
        <LegalListItem>
          Account details: name, email address, phone number, password (stored in hashed form).
        </LegalListItem>
        <LegalListItem>
          Purchase details: billing information, ticket selections, and communications with support.
        </LegalListItem>
        <LegalListItem>
          Organizer submissions: event details, images, and business contact information.
        </LegalListItem>
        <LegalListItem>Voluntary content: messages, feedback, or survey responses.</LegalListItem>
      </LegalList>

      <LegalSubheading>Information collected automatically</LegalSubheading>
      <LegalList>
        <LegalListItem>
          Device and browser data: IP address, browser type, operating system, and referring URLs.
        </LegalListItem>
        <LegalListItem>Usage data: pages viewed, clicks, session duration, and error logs.</LegalListItem>
        <LegalListItem>Cookies and similar technologies as described in Section 6.</LegalListItem>
      </LegalList>

      <LegalSubheading>Payment information</LegalSubheading>
      <p>
        Payments may be processed through third-party providers (e.g. M-Pesa, card processors). We receive
        transaction references, amounts, and status-not full payment instrument details where those are handled
        by the provider under their own privacy policies.
      </p>
    </LegalSection>

    <LegalSection title="2. How we use your information">
      <LegalList>
        <LegalListItem>Process ticket orders and deliver tickets (including email and QR codes).</LegalListItem>
        <LegalListItem>Create and manage user accounts and authenticate access.</LegalListItem>
        <LegalListItem>Communicate order confirmations, event updates, and service notices.</LegalListItem>
        <LegalListItem>Provide customer support and respond to inquiries.</LegalListItem>
        <LegalListItem>
          Share necessary attendee information with event Organizers for event management and entry.
        </LegalListItem>
        <LegalListItem>Detect, prevent, and investigate fraud, abuse, and security incidents.</LegalListItem>
        <LegalListItem>Improve the Platform, analytics, and user experience.</LegalListItem>
        <LegalListItem>Comply with legal obligations and enforce our Terms of Service.</LegalListItem>
        <LegalListItem>
          Send marketing communications where permitted; you may opt out at any time.
        </LegalListItem>
      </LegalList>
    </LegalSection>

    <LegalSection title="3. Legal bases for processing">
      <p>Where applicable, we process personal data based on:</p>
      <LegalList>
        <LegalListItem>Performance of a contract (e.g. fulfilling ticket purchases).</LegalListItem>
        <LegalListItem>
          Legitimate interests (e.g. security, fraud prevention, service improvement).
        </LegalListItem>
        <LegalListItem>Consent (e.g. optional marketing).</LegalListItem>
        <LegalListItem>Legal obligation (e.g. tax, regulatory, or law enforcement requests).</LegalListItem>
      </LegalList>
    </LegalSection>

    <LegalSection title="4. How we share information">
      <p>We may share personal information with:</p>
      <LegalList>
        <LegalListItem>
          <strong>Event Organizers</strong> - name, contact details, and ticket information needed to run the
          event.
        </LegalListItem>
        <LegalListItem>
          <strong>Payment processors</strong> - to complete transactions securely.
        </LegalListItem>
        <LegalListItem>
          <strong>Service providers</strong> - hosting, email delivery, analytics, and support tools under
          confidentiality obligations.
        </LegalListItem>
        <LegalListItem>
          <strong>Legal authorities</strong> - when required by law, court order, or to protect rights and
          safety.
        </LegalListItem>
        <LegalListItem>
          <strong>Business transfers</strong> - in connection with a merger, acquisition, or sale of assets,
          with appropriate safeguards.
        </LegalListItem>
      </LegalList>
      <p>We do not sell your personal information to third parties for their independent marketing.</p>
    </LegalSection>

    <LegalSection title="5. Data retention">
      <p>
        We retain personal information for as long as necessary to provide services, comply with legal and
        accounting requirements, resolve disputes, and enforce agreements. Ticket and transaction records
        may be retained for several years as required for tax, audit, and fraud-prevention purposes. When
        data is no longer needed, we delete or anonymize it where feasible.
      </p>
    </LegalSection>

    <LegalSection title="6. Cookies and tracking">
      <p>
        We use cookies and similar technologies to keep you signed in, remember preferences, measure traffic,
        and improve the Platform. You can control cookies through your browser settings; disabling cookies
        may limit certain features.
      </p>
    </LegalSection>

    <LegalSection title="7. Security">
      <p>
        We implement administrative, technical, and organizational measures designed to protect personal
        information, including encryption in transit (HTTPS), access controls, and secure password storage.
        No method of transmission or storage is completely secure; we cannot guarantee absolute security.
      </p>
    </LegalSection>

    <LegalSection title="8. Your rights">
      <p>Under the Kenya Data Protection Act and applicable law, you may have the right to:</p>
      <LegalList>
        <LegalListItem>Access personal information we hold about you.</LegalListItem>
        <LegalListItem>Request correction of inaccurate or incomplete data.</LegalListItem>
        <LegalListItem>Request deletion where legally permitted.</LegalListItem>
        <LegalListItem>Object to or restrict certain processing.</LegalListItem>
        <LegalListItem>Withdraw consent where processing is consent-based.</LegalListItem>
        <LegalListItem>
          Lodge a complaint with the Office of the Data Protection Commissioner (ODPC) in Kenya.
        </LegalListItem>
      </LegalList>
      <p>
        To exercise your rights, contact info@nikovibe.com. We may verify your identity before responding.
      </p>
    </LegalSection>

    <LegalSection title="9. Children">
      <p>
        The Platform is not directed at children under 13. We do not knowingly collect personal information
        from children under 13 without parental consent. If you believe we have collected such information,
        contact us so we can delete it.
      </p>
    </LegalSection>

    <LegalSection title="10. International transfers">
      <p>
        Your information may be processed in Kenya or in other countries where our service providers operate.
        Where data is transferred outside Kenya, we take steps to ensure appropriate safeguards consistent
        with applicable data protection law.
      </p>
    </LegalSection>

    <LegalSection title="11. Third-party links">
      <p>
        The Platform may link to third-party websites or event pages. We are not responsible for the privacy
        practices of those sites. Review their policies before providing personal information.
      </p>
    </LegalSection>

    <LegalSection title="12. Changes to this policy">
      <p>
        We may update this Privacy Policy from time to time. The &quot;Last updated&quot; date at the top will reflect
        revisions. Material changes may be communicated via the Platform or email where appropriate.
      </p>
    </LegalSection>
  </LegalDocumentLayout>
);

export default PrivacyPolicyPage;
